import { LogIn, LogOut } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

interface ClockEvent {
  id: string;
  eventType: 'clock_in' | 'clock_out';
  serverTs: string;
  gps: { lat: number; lng: number; accuracy: number };
  cfCity?: string | null;
  cfCountry?: string | null;
  note?: string | null;
}

interface ClockEventListProps {
  title: string;
  events: ClockEvent[];
  loading?: boolean;
  empty?: string;
}

export function ClockEventList({ title, events, loading, empty = 'No clock events yet.' }: ClockEventListProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? (
          <p className="text-sm text-brand-gray-mid">Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-brand-gray-mid">{empty}</p>
        ) : (
          <ul className="divide-y divide-brand-gray-light">
            {events.map((e) => {
              const isIn = e.eventType === 'clock_in';
              const Icon = isIn ? LogIn : LogOut;
              return (
                <li key={e.id} className="flex items-center gap-3 py-2.5">
                  <div
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                      isIn ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-warning/15 text-brand-warning',
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </div>
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-brand-charcoal">
                      {isIn ? 'Clocked in' : 'Clocked out'}
                      <span className="ml-2 text-brand-gray-mid">
                        {new Date(e.serverTs).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </span>
                    </p>
                    <p className="text-xs text-brand-gray-mid">
                      <span className="font-mono">
                        {e.gps.lat.toFixed(5)}, {e.gps.lng.toFixed(5)}
                      </span>
                      <span className="mx-1.5">·</span>
                      ±{Math.round(e.gps.accuracy)}m
                      {(e.cfCity || e.cfCountry) && (
                        <>
                          <span className="mx-1.5">·</span>
                          {[e.cfCity, e.cfCountry].filter(Boolean).join(', ')}
                        </>
                      )}
                    </p>
                    {e.note && (
                      <p className="mt-0.5 text-xs italic text-brand-charcoal/70">"{e.note}"</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

export type { ClockEvent };
